import React, { useEffect, useRef, useState } from 'react';
import { adminApi, apiError } from '../../lib/adminApi';
import { toast } from 'sonner';
import { Upload, Loader2, Users, MailCheck, MailX, Search } from 'lucide-react';

const StatCard = ({ icon, label, value, testid }) => (
  <div className="bg-[#0d0d0d] border border-white/10 rounded-xl p-5" data-testid={testid}>
    <div className="flex items-center gap-3 text-white/50 text-xs uppercase tracking-widest">{icon}{label}</div>
    <div className="text-3xl font-black text-white mt-2">{value?.toLocaleString('el-GR') ?? '—'}</div>
  </div>
);

export default function CustomersPanel() {
  const [stats, setStats] = useState(null);
  const [region, setRegion] = useState('ALL');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 25 });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadStats = () => adminApi.get('/admin/customers/stats').then((r) => setStats(r.data)).catch(() => {});

  const loadCustomers = (page = 1) => {
    setLoading(true);
    adminApi.get('/admin/customers', { params: { region, search: search || undefined, page, limit: 25 } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadCustomers(1); /* eslint-disable-next-line */ }, [region]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const { data: res } = await adminApi.post('/admin/customers/import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Importati ${res.imported} nuovi, aggiornati ${res.updated}. Totale: ${res.total}`);
      loadStats();
      loadCustomers(1);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.limit));

  return (
    <div className="space-y-6" data-testid="customers-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={<Users size={14} />} label="Clienti totali" value={stats?.total} testid="stat-total" />
        <StatCard icon={<MailCheck size={14} />} label="Iscritti" value={stats?.subscribed} testid="stat-subscribed" />
        <StatCard icon={<MailX size={14} />} label="Disiscritti" value={stats?.unsubscribed} testid="stat-unsubscribed" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-3">
          <select
            data-testid="region-filter"
            value={region} onChange={(e) => setRegion(e.target.value)}
            className="bg-black border border-white/15 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-red-500"
          >
            <option value="ALL">Tutte le regioni ({stats?.total ?? 0})</option>
            {stats?.by_region?.map((r) => (
              <option key={r.region} value={r.region}>{r.region} ({r.count})</option>
            ))}
          </select>
          <div className="flex items-center bg-black border border-white/15 rounded-lg px-3">
            <Search size={16} className="text-white/40" />
            <input
              data-testid="customer-search"
              value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadCustomers(1)}
              placeholder="Cerca nome, email, città…"
              className="bg-transparent px-2 py-2 text-white text-sm outline-none w-56"
            />
          </div>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" onChange={onUpload} className="hidden" data-testid="upload-file-input" />
          <button
            data-testid="upload-customers-button"
            onClick={() => fileRef.current?.click()} disabled={uploading}
            className="bg-red-600 hover:bg-red-500 transition-colors text-white font-bold text-sm px-4 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-60"
          >
            {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
            Carica Excel clienti
          </button>
        </div>
      </div>

      <div className="bg-[#0d0d0d] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="text-left text-white/40 text-xs uppercase tracking-wider border-b border-white/10">
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Regione</th>
                <th className="px-4 py-3">Città</th>
                <th className="px-4 py-3">Stato</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-white/40"><Loader2 className="animate-spin inline" /></td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-white/40">Nessun cliente. Carica il file Excel per iniziare.</td></tr>
              ) : data.items.map((c) => (
                <tr key={c.id} className="border-b border-white/5 text-white/80 hover:bg-white/5">
                  <td className="px-4 py-3">{c.first_name} {c.last_name}</td>
                  <td className="px-4 py-3 text-white/60">{c.email}</td>
                  <td className="px-4 py-3">{c.region}</td>
                  <td className="px-4 py-3 text-white/60">{c.city}</td>
                  <td className="px-4 py-3">
                    {c.subscribed
                      ? <span className="text-green-400 text-xs">Iscritto</span>
                      : <span className="text-red-400 text-xs">Disiscritto</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 text-xs text-white/50 border-t border-white/10">
            <span>{data.total.toLocaleString('el-GR')} clienti · pagina {data.page}/{totalPages}</span>
            <div className="flex gap-2">
              <button disabled={data.page <= 1} onClick={() => loadCustomers(data.page - 1)}
                className="px-3 py-1 border border-white/15 rounded disabled:opacity-40 hover:border-red-500">‹</button>
              <button disabled={data.page >= totalPages} onClick={() => loadCustomers(data.page + 1)}
                className="px-3 py-1 border border-white/15 rounded disabled:opacity-40 hover:border-red-500">›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
